import { ToggleGroup, Options, Menu } from '@instructure/ui';
import InfoIcon from '@ui/shared/components/Common/InfoIcon';
import {
  Badge,
  Flex,
  View,
  IconExternalLinkLine,
  IconButton,
  IconSettingsLine,
  Text,
} from '@smarterservices/smarter-ui';
import useAppStore from '../../stores/app';
import useSessionStore from '../../stores/session';
import WaitingTimer from './WaitingTimer';
import RenderName from './RenderName';
import TransferNote from './TransferNote';

/**
 * AwaitingCheckin Component
 * 
 * Displays a collapsible list of sessions that are awaiting check-in.
 * Shows a timer for how long each session has been waiting, the student's name,
 * and provides options to start check-in or transfer the session.
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onMenuDismiss - Callback to close the parent popover
 * @returns {JSX.Element} The awaiting check-in component
 */
const AwaitingCheckin = ({ onMenuDismiss }) => {
  const waiting = useSessionStore((state) => state.waiting);
  const setTransferSessionSid = useSessionStore((state) => state.setTransferSessionSid);

  const waitingCount = Object.keys(waiting).length;

  /**
   * Renders the options menu for a session
   * @param {string} sessionSid - The session identifier
   * @returns {JSX.Element} Menu with check-in and transfer options
   */
  const renderOptionsMenu = (sessionSid) => {
    return (
      <Menu
        placement="bottom end"
        shouldContainFocus
        shouldReturnFocus
        offsetX="-5"
        mountNode={() => document.body}
        onDismiss={onMenuDismiss}
        trigger={
          <IconButton
            withBackground={false}
            withBorder={false}
            color="primary"
            size="small"
            screenReaderLabel="Session Options"
            margin="none"
            padding="none"
          >
            <IconSettingsLine />
          </IconButton>
        }
      >
        {/* Start Check-in option */}
        <Menu.Item onSelect={() => useAppStore.getState().openDrawer('focus', sessionSid)}>
          <Flex alignItems="center">
            <Flex.Item margin="0 xx-small 0 0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={24}
                height={24}
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M17.55 11.289L14.712 8.45l.713-.688l2.125 2.125l4.25-4.25l.708.713zM9 11.385q-1.237 0-2.119-.882T6 8.385t.881-2.12T9 5.386t2.119.88t.881 2.12t-.881 2.118T9 11.385m-7 7.23V16.97q0-.619.36-1.158q.361-.54.97-.838q1.416-.679 2.834-1.018q1.417-.34 2.836-.34t2.837.34t2.832 1.018q.61.298.97.838q.361.539.361 1.158v1.646zm1-1h12v-.646q0-.332-.215-.625q-.214-.292-.593-.494q-1.234-.598-2.545-.916T9 14.616t-2.646.318t-2.546.916q-.38.202-.593.494Q3 16.637 3 16.97zm6-7.23q.825 0 1.413-.588T11 8.384t-.587-1.412T9 6.384t-1.412.588T7 8.384t.588 1.413T9 10.384m0-2"
                />
              </svg>
            </Flex.Item>
            <Flex.Item>
              <Text>Start Check-in</Text>
            </Flex.Item>
          </Flex>
        </Menu.Item>

        <Menu.Separator />

        {/* Transfer option */}
        <Menu.Item onSelect={() => setTransferSessionSid(sessionSid)}>
          <Flex alignItems="center">
            <Flex.Item margin="0 xx-small 0 0">
              <IconExternalLinkLine size="x-small" />
            </Flex.Item>
            <Flex.Item>
              <Text>Transfer</Text>
            </Flex.Item>
          </Flex>
        </Menu.Item>
      </Menu>
    );
  };

  return (
    <ToggleGroup
      toggleLabel="Sessions that are awaiting checkin"
      padding="0 0 xxx-small 0"
      summary={
        <Flex justifyItems="space-between" margin="none small none none">
          <Flex.Item>Awaiting Check In <InfoIcon margin="0 0 xxx-small xx-small" color="secondary" tip="This includes sessions that are waiting to be checked in." size="xx-small" /></Flex.Item>
          {waitingCount > 0 && (
            <Flex.Item>
              <Badge
                count={waitingCount}
                variant="danger"
                pulse={true}
                standalone
              />
            </Flex.Item>
          )}
        </Flex>
      }
      defaultExpanded
    >
      {waitingCount > 0 ? (
        <Options>
          {Object.entries(waiting).map(([sessionSid, data]) => (
            <Options.Item
              key={`session_waiting_${sessionSid}`}
              beforeLabelContentVAlign="start"
              afterLabelContentVAlign="start"
            >
              <Flex justifyItems="space-between" padding="none">
                {/* Waiting timer */}
                <Flex.Item width="3.2rem">
                  <WaitingTimer startTime={data.routedAt} />
                </Flex.Item>

                {/* Student name */}
                <Flex.Item
                  margin="0 0 0 xx-small"
                  textAlign="start"
                  maxWidth="65%"
                  shouldGrow
                >
                  <RenderName
                    firstName={data.firstName}
                    lastName={data.lastName}
                  />
                </Flex.Item>

                {/* Transfer note indicator (if present) */}
                <TransferNote note={data.transfer?.note} />

                {/* Options menu */}
                <Flex.Item width="2rem" textAlign="end">
                  {renderOptionsMenu(sessionSid)}
                </Flex.Item>
              </Flex>
            </Options.Item>
          ))}
        </Options>
      ) : (
        <View as="div" padding="small" textAlign="center">
          <Text>No sessions waiting</Text>
        </View>
      )}
    </ToggleGroup>
  );
};

export default AwaitingCheckin;
